
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)').matches) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    this.mainDetailsToggle.setAttribute('open', '');
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.mainDetailsToggle.setAttribute('open', '');
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      this.dispatchProductSelectEvent();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  getAllSelectedOptions() {
    const options = [];
    this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
      if (checked) {
        options.push({ name: checked.dataset.optionName || '', value: checked.value });
      }
    });
    return options;
  }

  dispatchProductSelectEvent() {
    const { ProductSelectEvent } = window.StandardEvents || {};
    if (!ProductSelectEvent) return;

    const deferred = ProductSelectEvent.createPromise();
    this.pendingSelectPromise = deferred;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title: this.dataset.productTitle,
          handle: this.dataset.productHandle,
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferred.promise,
      })
    );
  }

  takePendingSelectPromise() {
    const deferred = this.pendingSelectPromise;
    this.pendingSelectPromise = null;
    return deferred;
  }

  resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
    const deferred = this.takePendingSelectPromise();
    if (!deferred) return;

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale: variant.available,
          price: {
            amount: sourceVariantSelects?.dataset.selectedPriceAmount,
            currencyCode: sourceVariantSelects?.dataset.currencyCode,
          },
          selectedOptions: this.getAllSelectedOptions(),
        },
      });
    } else {
      deferred.resolve({ variant: null });
    }
  }

  rejectPendingSelectPromise(error) {
    this.takePendingSelectPromise()?.reject(error);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  getCartQuantityForLine(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    return parseInt(input?.dataset.cartQuantity || input?.getAttribute('value') || '0', 10) || 0;
  }

  startCartLinesUpdate(items) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    const linesByAction = Object.entries(items).reduce((groups, [variantId, quantity]) => {
      const nextQuantity = parseInt(quantity, 10);
      const currentQuantity = this.getCartQuantityForLine(variantId);

      if (Number.isNaN(nextQuantity) || currentQuantity === nextQuantity) return groups;

      const action = currentQuantity === 0 ? 'add' : nextQuantity === 0 ? 'remove' : 'update';
      let line;
      if (action === 'add') {
        line = { merchandiseId: variantId, quantity: nextQuantity };
      } else {
        const lineKey = this.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.dataset.quantityLineKey;
        // No AJAX line key on the row — likely cached HTML rendered before this
        // attribute landed. Skip rather than emit an event with id: ''.
        if (!lineKey) return groups;
        line = { id: lineKey, quantity: nextQuantity };
      }

      if (!groups[action]) groups[action] = [];
      groups[action].push(line);
      return groups;
    }, {});

    const deferreds = Object.entries(linesByAction).map(([action, lines]) => {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'product',
          lines,
          promise: deferred.promise,
        })
      );
      return deferred;
    });

    return {
      resolve: (parsedState) => {
        const payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) };
        deferreds.forEach((deferred) => deferred.resolve(payload));
      },
      reject: (error) => {
        deferreds.forEach((deferred) => deferred.reject(error));
      },
    };
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;

    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
document.addEventListener("scroll", () => {
  const heroImage = document.querySelector(".hero-scroll-image");
  const heroContent = document.querySelector(".hero-scroll-content");

  if (!heroImage) return;

  const hero = heroImage.closest(".hero-sticky");

  if (!hero) return;

  const rect = hero.getBoundingClientRect();
  const height = hero.offsetHeight;

  let progress = -rect.top / height;

  progress = Math.min(Math.max(progress, 0), 1);


  // Afbeelding effect
  heroImage.style.transform =
    `scale(${1 + progress * 0.08})`;

  heroImage.style.filter =
    `brightness(${1 - progress * 0.2})`;


  // Tekst effect
  if (heroContent) {
    heroContent.style.opacity = 1 - progress;

    heroContent.style.transform =
      `translateY(${progress * 50}px)`;
  }

});


document.addEventListener("DOMContentLoaded", function () {
  const products = document.querySelectorAll(".scroll-product");

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add("show");
          }, index * 100);
        } else {
          // One-time reveal: keep the product visible after its first entrance.
        }
      });
    },
    {
      threshold: 0.15
    }
  );

  products.forEach(product => {
    observer.observe(product);
  });
});

document.addEventListener('DOMContentLoaded', () => {
  const brandTitle = document.querySelector('.hero-brand-title');
  const banner = document.querySelector('.banner');

  if (!brandTitle || !banner) return;

  document.body.appendChild(brandTitle);

  const updateBrandTitle = () => {
    const bannerRect = banner.getBoundingClientRect();
    const bannerHeight = banner.offsetHeight;

    const progress = Math.min(
      Math.max(-bannerRect.top / (bannerHeight * 0.65), 0),
      1
    );

    const startY = window.innerHeight * 0.5;
    const endY = 27;
    const currentY = startY + (endY - startY) * progress;

    const startScale = 1;
    const baseFontSize =
      parseFloat(window.getComputedStyle(brandTitle).fontSize) || 100;
    const dockedFontSize = window.innerWidth < 750 ? 20 : 28;
    const endScale = Math.min(dockedFontSize / baseFontSize, 1);
    const currentScale = startScale + (endScale - startScale) * progress;

    brandTitle.style.position = 'fixed';
    brandTitle.style.top = `${currentY}px`;
    brandTitle.style.left = '50%';
    brandTitle.style.transform =
      `translate(-50%, -50%) scale(${currentScale})`;
      const shade = Math.round(255 * (1 - progress));
brandTitle.style.color = `rgb(${shade}, ${shade}, ${shade})`;
  };

  updateBrandTitle();
  window.addEventListener('scroll', updateBrandTitle, { passive: true });
  window.addEventListener('resize', updateBrandTitle);
});



document.addEventListener('DOMContentLoaded', () => {
  const title = document.querySelector('.hero-brand-title');
  const banner = document.querySelector('.banner');

  if (!title || !banner) return;

  title.setAttribute('role', 'link');
  title.setAttribute('tabindex', '0');
  title.setAttribute('aria-label', 'Go to homepage');

 const goHome = () => {
  const destination =
    window.Shopify?.routes?.root || '/';

  if (typeof window.runRakiTransition === 'function') {
    window.runRakiTransition(destination);
  } else {
    window.location.assign(destination);
  }
};

  title.addEventListener('click', goHome);
  title.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      goHome();
    }
  });

  const updateDockedHeader = () => {
    const bannerHeight = banner.offsetHeight;
    const progress = Math.min(
      Math.max(-banner.getBoundingClientRect().top / (bannerHeight * 0.65), 0),
      1
    );

    document.body.classList.toggle('hero-brand-docked', progress >= 0.85);
  };

  updateDockedHeader();
  window.addEventListener('scroll', updateDockedHeader, { passive: true });
  window.addEventListener('resize', updateDockedHeader);
});


document.addEventListener('DOMContentLoaded', () => {
  const banner = document.querySelector('.banner');
  const headerBrand =
    document.querySelector('.header__heading') ||
    document.querySelector('.header > .header__heading-link');
  const headerMenu = document.querySelector('.header__inline-menu');

  if (!banner || !headerBrand) return;

  let menuShift = 0;
  let frameRequested = false;

  const measureMenuShift = () => {
    if (!headerMenu) return;

    headerBrand.style.transform = 'translate3d(0, 0, 0)';
    headerMenu.style.transform = 'translate3d(0, 0, 0)';

    const brandRect = headerBrand.getBoundingClientRect();
    const menuRect = headerMenu.getBoundingClientRect();
    menuShift = Math.max(0, menuRect.left - brandRect.left);
  };

  const renderHeaderTransition = () => {
    frameRequested = false;

    const bannerHeight = Math.max(banner.offsetHeight, 1);
    const progress = Math.min(
      Math.max(-banner.getBoundingClientRect().top / (bannerHeight * 0.65), 0),
      1
    );

    let slideProgress = Math.min(Math.max((progress - 0.45) / 0.55, 0), 1);
    slideProgress =
      slideProgress * slideProgress * (3 - 2 * slideProgress);

    headerBrand.style.opacity = String(1 - slideProgress);
    headerBrand.style.transform =
      'translate3d(' + (-24 * slideProgress) + 'px, 0, 0)';
    headerBrand.style.pointerEvents =
      slideProgress > 0.9 ? 'none' : 'auto';

    if (headerMenu) {
      headerMenu.style.justifySelf = 'start';
      headerMenu.style.marginLeft = '0';
      headerMenu.style.transform =
        'translate3d(' + (-menuShift * slideProgress) + 'px, 0, 0)';
    }
  };

  const requestRender = () => {
    if (frameRequested) return;
    frameRequested = true;
    requestAnimationFrame(renderHeaderTransition);
  };

  const handleResize = () => {
    measureMenuShift();
    requestRender();
  };

  measureMenuShift();
  renderHeaderTransition();

  window.addEventListener('scroll', requestRender, { passive: true });
  window.addEventListener('resize', handleResize);
});

document.addEventListener('click', (event) => {
  const button = event.target.closest(
    '[data-product-card-gallery-button]'
  );

  if (!button) return;

  event.preventDefault();
  event.stopPropagation();

  const gallery = button.closest('[data-product-card-gallery]');
  const cardMedia = gallery.closest('.card__media');
  const image = cardMedia.querySelector('.media > img');
  const imageData = gallery.querySelector(
    '[data-product-card-gallery-images]'
  );

  if (!image || !imageData) return;

  let images;

  try {
    images = JSON.parse(imageData.textContent);
  } catch (error) {
    console.error('Productfoto’s konden niet worden gelezen.', error);
    return;
  }

  if (images.length < 2) return;

  const direction = Number(button.dataset.direction);
  const currentIndex = Number(gallery.dataset.currentIndex || 0);
  const nextIndex =
    (currentIndex + direction + images.length) % images.length;

  const nextImage = images[nextIndex];

  image.removeAttribute('srcset');
  image.src = nextImage.src;
  image.alt = nextImage.alt || '';

  gallery.dataset.currentIndex = nextIndex;

  image.animate(
    [
      { opacity: 0.45 },
      { opacity: 1 }
    ],
    {
      duration: 180,
      easing: 'ease-out'
    }
  );
});

/* Touch product-card gallery: swipe horizontally over any product image to
   move through its media without opening the product. Event delegation keeps
   this working for asynchronously loaded recommendations as well. */
(() => {
  let gesture = null;
  let suppressedCard = null;
  let suppressClickUntil = 0;

  const showGalleryImage = (gallery, direction) => {
    const image = gallery.closest('.card__media')?.querySelector('.media > img:first-of-type');
    const imageData = gallery.querySelector('[data-product-card-gallery-images]');
    if (!image || !imageData) return false;

    let images;
    try {
      images = JSON.parse(imageData.textContent.trim());
    } catch (error) {
      return false;
    }

    if (!Array.isArray(images) || images.length < 2) return false;

    const currentIndex = Number(gallery.dataset.currentIndex || 0);
    const nextIndex = (currentIndex + direction + images.length) % images.length;
    const nextImage = images[nextIndex];

    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = nextImage.src;
    image.alt = nextImage.alt || '';
    gallery.dataset.currentIndex = String(nextIndex);

    image.animate(
      [{ opacity: 0.55, transform: `translateX(${direction > 0 ? '2%' : '-2%'})` }, { opacity: 1, transform: 'translateX(0)' }],
      { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' }
    );

    return true;
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      const card = event.target.closest('.card-wrapper');
      const gallery = card?.querySelector('[data-product-card-gallery]');
      const media = gallery?.closest('.card__media');
      if (!card || !gallery || !media) return;

      const bounds = media.getBoundingClientRect();
      if (
        event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom
      ) return;

      gesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        card,
        gallery
      };
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;

      const activeGesture = gesture;
      gesture = null;

      const deltaX = event.clientX - activeGesture.startX;
      const deltaY = event.clientY - activeGesture.startY;
      const distance = Math.abs(deltaX);

      if (distance < 42 || distance <= Math.abs(deltaY) * 1.15) return;

      const changed = showGalleryImage(activeGesture.gallery, deltaX < 0 ? 1 : -1);
      if (!changed) return;

      event.preventDefault();
      event.stopPropagation();
      suppressedCard = activeGesture.card;
      suppressClickUntil = Date.now() + 500;
    },
    { capture: true }
  );

  document.addEventListener(
    'pointercancel',
    () => {
      gesture = null;
    },
    { passive: true }
  );

  document.addEventListener(
    'click',
    (event) => {
      if (Date.now() > suppressClickUntil || !suppressedCard) return;
      if (event.target.closest('.card-wrapper') !== suppressedCard) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );
})();

/* Product cards: show the campaign/model image on hover, then restore the packshot. */
document.addEventListener('DOMContentLoaded', () => {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  document.querySelectorAll('[data-product-card-gallery]').forEach((gallery) => {
    const card = gallery.closest('.card-wrapper');
    const image = gallery.closest('.card__media')?.querySelector('.media > img:first-of-type');
    const imageData = gallery.querySelector('[data-product-card-gallery-images]');
    if (!card || !image || !imageData) return;

    let images;
    try {
      images = JSON.parse(imageData.textContent.trim());
    } catch (error) {
      return;
    }
    if (!Array.isArray(images) || images.length < 2) return;

    /* The final image is the campaign/model shot when supplied; otherwise use angle two. */
    const hoverImage = images.length >= 3 ? images[images.length - 1] : images[1];
    const original = {
      src: image.currentSrc || image.src,
      srcAttribute: image.getAttribute('src'),
      srcset: image.getAttribute('srcset'),
      sizes: image.getAttribute('sizes'),
      alt: image.alt
    };
    const preloader = new Image();
    preloader.src = hoverImage.src;

    const setImage = (source, isHover) => {
      image.style.opacity = '0';
      window.setTimeout(() => {
        if (isHover) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = source.src;
          image.alt = source.alt || '';
        } else {
          image.src = source.srcAttribute || source.src;
          if (source.srcset) image.setAttribute('srcset', source.srcset);
          else image.removeAttribute('srcset');
          if (source.sizes) image.setAttribute('sizes', source.sizes);
          else image.removeAttribute('sizes');
          image.alt = source.alt || '';
        }
        gallery.dataset.currentIndex = isHover ? String(images.indexOf(hoverImage)) : '0';
        card.classList.toggle('is-raki-hover-image', isHover);
        image.style.opacity = '1';
      }, 90);
    };

    card.addEventListener('mouseenter', () => setImage(hoverImage, true));
    card.addEventListener('mouseleave', () => setImage(original, false));
  });
});

/* Desktop product gallery: wheel over the garment advances media without moving the page. */
document.addEventListener('DOMContentLoaded', () => {
  if (!window.matchMedia('(min-width: 990px) and (pointer: fine)').matches) return;

  document.querySelectorAll('.raki-v3__media').forEach((viewer) => {
    const list = viewer.querySelector('.product__media-list');
    const slides = Array.from(viewer.querySelectorAll('.product__media-item'));
    if (!list || slides.length < 2) return;

    viewer.dataset.rakiWheelGallery = 'true';
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let locked = false;

    const rail = document.createElement('div');
    rail.className = 'raki-wheel-rail';
    rail.setAttribute('aria-label', 'Product media');

    const buttons = slides.map((slide, index) => {
      const source = slide.querySelector('img');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'raki-wheel-rail__button';
      button.setAttribute('aria-label', `Show product image ${index + 1}`);
      if (source) {
        const thumb = source.cloneNode(false);
        thumb.removeAttribute('srcset');
        thumb.removeAttribute('sizes');
        thumb.src = source.currentSrc || source.src;
        thumb.alt = '';
        button.appendChild(thumb);
      }
      button.addEventListener('click', () => goTo(index));
      rail.appendChild(button);
      return button;
    });

    const hint = document.createElement('span');
    hint.className = 'raki-wheel-hint';
    hint.textContent = 'Scroll to explore';
    viewer.appendChild(rail);
    viewer.appendChild(hint);

    const updateState = () => {
      slides.forEach((slide, index) => slide.classList.toggle('is-active', index === activeIndex));
      buttons.forEach((button, index) => {
        button.classList.toggle('is-active', index === activeIndex);
        button.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });
    };

    function goTo(index) {
      activeIndex = Math.max(0, Math.min(slides.length - 1, index));
      list.scrollTo({ top: slides[activeIndex].offsetTop, behavior: 'smooth' });
      updateState();
    }

    viewer.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaY) < 4 && Math.abs(event.deltaX) < 4) return;
      event.preventDefault();
      if (locked) return;
      locked = true;
      const direction = (Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX) > 0 ? 1 : -1;
      const nextIndex = (activeIndex + direction + slides.length) % slides.length;
      goTo(nextIndex);
      window.setTimeout(() => { locked = false; }, 520);
    }, { passive: false });

    updateState();
  });
});





document.addEventListener('DOMContentLoaded', () => {
  const updateHeaderTransparency = () => {
    const announcementHeight =
      document.querySelector('.announcement-bar-section')?.offsetHeight || 39;
    document.documentElement.style.setProperty(
      '--raki-announcement-height',
      announcementHeight + 'px'
    );
    const isScrolled = window.scrollY >= announcementHeight;
    document.body.classList.toggle('raki-header-scrolled', isScrolled);

    const banner = document.querySelector(
      '#MainContent > .shopify-section:first-child .banner'
    );
    const headerHeight =
      document.querySelector('.section-header')?.offsetHeight || 0;
    const isPastBanner = banner
      ? banner.getBoundingClientRect().bottom <= headerHeight
      : false;

    document.body.classList.toggle(
      'raki-header-past-banner',
      isPastBanner
    );

    document
      .querySelectorAll('.header__heading-link, .header__menu-item, .header__icon')
      .forEach((item) => {
        if (isScrolled) {
          item.style.removeProperty('color');
        } else {
          item.style.removeProperty('color');
        }
      });
  };

  updateHeaderTransparency();
  window.addEventListener('scroll', updateHeaderTransparency, { passive: true });
});

// Product card gallery: handle arrow clicks before Dawn's card link.
document.addEventListener(
  'click',
  async (event) => {
    const button = event.target.closest('[data-product-card-gallery-button]');
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const gallery = button.closest('[data-product-card-gallery]');
    const cardMedia = gallery?.closest('.card__media');
    const image = cardMedia?.querySelector('.media > img:first-of-type');
    const imageData = gallery?.querySelector('[data-product-card-gallery-images]');

    if (!gallery || !image || !imageData) return;
    if (gallery.dataset.animating === 'true') return;

    let images;

    try {
      images = JSON.parse(imageData.textContent.trim());
    } catch (error) {
      console.error('Product card gallery images could not be read.', error);
      return;
    }

    if (!Array.isArray(images) || images.length < 2) return;

    const direction = Number(button.dataset.direction) || 1;
    const currentIndex = Number(gallery.dataset.currentIndex || 0);
    const nextIndex =
      (currentIndex + direction + images.length) % images.length;
    const nextImage = images[nextIndex];
    const media = image.parentElement;

    if (!media || !nextImage?.src) return;

    gallery.dataset.animating = 'true';

    const commitNextImage = async () => {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = nextImage.src;
      image.alt = nextImage.alt || '';

      if (typeof image.decode === 'function') {
        await image.decode().catch(() => {});
      }

      gallery.dataset.currentIndex = String(nextIndex);
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      try {
        await commitNextImage();
      } finally {
        delete gallery.dataset.animating;
      }
      return;
    }

    const incomingImage = image.cloneNode(false);
    const computedImageStyle = window.getComputedStyle(image);
    let outgoingAnimation;
    let incomingAnimation;

    incomingImage.removeAttribute('srcset');
    incomingImage.removeAttribute('sizes');
    incomingImage.removeAttribute('loading');
    incomingImage.removeAttribute('fetchpriority');
    incomingImage.src = nextImage.src;
    incomingImage.alt = '';
    incomingImage.setAttribute('aria-hidden', 'true');
    incomingImage.classList.add('product-card-gallery__transition-image');

    Object.assign(incomingImage.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      objectFit: computedImageStyle.objectFit,
      objectPosition: computedImageStyle.objectPosition,
      pointerEvents: 'none',
      zIndex: '2',
      opacity: '1',
      transform: 'translateX(' + direction * 100 + '%)',
      willChange: 'transform'
    });

    media.appendChild(incomingImage);

    try {
      if (typeof incomingImage.decode === 'function') {
        await incomingImage.decode().catch(() => {});
      }

      const timing = {
        duration: 420,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards'
      };

      outgoingAnimation = image.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(' + -direction * 100 + '%)' }
        ],
        timing
      );

      incomingAnimation = incomingImage.animate(
        [
          { transform: 'translateX(' + direction * 100 + '%)' },
          { transform: 'translateX(0)' }
        ],
        timing
      );

      await Promise.all([
        outgoingAnimation.finished,
        incomingAnimation.finished
      ]).catch(() => {});

      await commitNextImage();
    } catch (error) {
      console.error('Product card gallery transition failed.', error);
    } finally {
      outgoingAnimation?.cancel();
      incomingAnimation?.cancel();
      incomingImage.remove();
      delete gallery.dataset.animating;
    }
  },
  true
);


// Keep regular image clicks opening the product after raising the media layer.
document.addEventListener('click', (event) => {
  const cardInner = event.target.closest('.card__inner');

  if (!cardInner || !cardInner.querySelector('[data-product-card-gallery]')) return;
  if (event.target.closest('[data-product-card-gallery-button]')) return;

  const productLink = cardInner
    .closest('.card')
    ?.querySelector('.card__heading a.full-unstyled-link');

  if (productLink) productLink.click();
});

/* =========================================================
   RAKI STUDIOS — CONTROLLED PAGE TRANSITIONS

   ONLY:
   - product clicks
   - header logo clicks
   - hero RAKI STUDIOS clicks

   Initial load / refresh blijft via de bestaande
   loader in theme.liquid lopen.
   ========================================================= */

(() => {

  let transitionRunning = false;

  function runRakiTransition(destination) {
    if (transitionRunning) return;

    transitionRunning = true;

    /* oude overlay voor zekerheid verwijderen */
    document
      .querySelectorAll('.raki-page-transition')
      .forEach(el => el.remove());

    const transition = document.createElement('div');

    transition.className = 'raki-page-transition';

    transition.innerHTML = `
      <div class="raki-page-transition__logo">
        RAKI STUDIOS
      </div>
    `;

    document.body.appendChild(transition);

    /*
     * Eerst onder het scherm renderen,
     * daarna smooth omhoog.
     */
    void transition.offsetHeight;
    transition.classList.add('is-entering');

    /*
     * Wachten totdat scherm viewport bedekt.
     */
    let navigationStarted = false;
    const continueNavigation = () => {
      if (navigationStarted) return;
      navigationStarted = true;
      try {
        sessionStorage.setItem('raki-transition-arrival', 'true');
      } catch (e) {}
      window.location.href = destination;
    };

    transition.addEventListener(
      'transitionend',
      (event) => {
        if (event.target === transition && event.propertyName === 'transform') {
          continueNavigation();
        }
      },
      { once: true }
    );

    /* Fallback for browsers that skip transitionend. */
    window.setTimeout(continueNavigation, 800);
  }


  document.addEventListener(
    'click',
    (event) => {

      const link = event.target.closest('a[href]');

      if (!link) return;

      /* Laat speciale browser-clicks gewoon werken */
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        link.target === '_blank' ||
        link.hasAttribute('download')
      ) {
        return;
      }


      let destination;

      try {
        destination = new URL(
          link.href,
          window.location.href
        );
      } catch {
        return;
      }


      /* Alleen eigen website */
      if (
        destination.origin !==
        window.location.origin
      ) {
        return;
      }


      /* =====================================
         ALLOWED #1 — PRODUCT
         ===================================== */

      const isProduct =
        destination.pathname.startsWith('/products/');


      /* =====================================
         ALLOWED #2 — HEADER LOGO
         ===================================== */

      const isHeaderLogo =
        link.matches('.header__heading-link') ||
        !!link.closest('.header__heading');


      /*
       * Alles behalve deze twee:
       * GEEN transition.
       */

      if (!isProduct && !isHeaderLogo && destination.pathname !== '/') {
        return;
      }


      event.preventDefault();

      runRakiTransition(destination.href);

    },
    true
  );


  /*
   * Expose zodat je grote hero RAKI STUDIOS
   * dezelfde animation kan gebruiken.
   */
  window.runRakiTransition = runRakiTransition;

})();

/* =========================================================
   RAKI — CLOSE MEGA MENU DIRECTLY BELOW VISIBLE MENU
   ========================================================= */

document.addEventListener('mousemove', function (e) {
  if (window.innerWidth < 990) return;

  const menus = [
    document.querySelector('#RakiProductsMenu'),
    document.querySelector('#RakiCollectionsMenu')
  ].filter(Boolean);

  const openMenu = menus.find(menu => {
    const style = window.getComputedStyle(menu);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      parseFloat(style.opacity || '1') > 0
    );
  });

  if (!openMenu) return;

  const rect = openMenu.getBoundingClientRect();

  /* Zodra cursor ONDER de zichtbare menu-balk komt */
  if (e.clientY > rect.bottom) {

    /* sluit alle details */
    document.querySelectorAll(
      '.raki-products-details, .raki-collections-details, header details'
    ).forEach(details => {
      details.removeAttribute('open');
    });

    /* verwijder custom open-state */
    document.body.classList.remove('raki-mega-open');

    /* menu direct verbergen */
    menus.forEach(menu => {
      menu.style.setProperty('opacity', '0', 'important');
      menu.style.setProperty('visibility', 'hidden', 'important');
      menu.style.setProperty('pointer-events', 'none', 'important');
    });
  }
});

document.querySelectorAll(
  '.raki-products-details, .raki-collections-details'
).forEach(trigger => {

  trigger.addEventListener('mouseenter', () => {

    document.querySelectorAll(
      '#RakiProductsMenu, #RakiCollectionsMenu'
    ).forEach(menu => {
      menu.style.removeProperty('opacity');
      menu.style.removeProperty('visibility');
      menu.style.removeProperty('pointer-events');
    });

  });

});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll(
    '.raki-loader__logo, .raki-page-transition__logo, .raki-loader-logo, .raki-transition-logo'
  ).forEach(el => {
    el.textContent = 'RAKI STUDIOS';
  });
});

/* =========================================================
   RAKI STUDIOS — SKIP INTRO AFTER INTERNAL NAVIGATION
   ========================================================= */

document.addEventListener(
  'click',
  (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;

    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.target === '_blank' ||
      link.hasAttribute('download')
    ) {
      return;
    }

    let destination;

    try {
      destination = new URL(link.href, window.location.href);
    } catch {
      return;
    }

    /* Alleen links binnen RAKI STUDIOS */
    if (destination.origin !== window.location.origin) return;

    /*
     * De VOLGENDE pagina mag zijn gewone intro niet tonen.
     *
     * Product / logo krijgen hun eigen slide-transition.
     * Collections / account / andere links gaan direct.
     */
    sessionStorage.setItem('rakiSkipIntroOnce', '1');
  },
  true
);

/* =========================================================
   RAKI STUDIOS — MARK NORMAL INTERNAL NAVIGATION
   ========================================================= */

document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href]');
  if (!link) return;

  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    link.target === '_blank' ||
    link.hasAttribute('download')
  ) return;

  let url;

  try {
    url = new URL(link.href, window.location.href);
  } catch {
    return;
  }

  if (url.origin !== window.location.origin) return;

  const isHeaderLogo =
    link.matches('.header__heading-link') ||
    !!link.closest('.header__heading');

  const isProduct =
    url.pathname.startsWith('/products/');

  /*
   * Logo + products mogen transition houden.
   * ALLE andere interne navigatie:
   * opening-loader op volgende pagina overslaan.
   */
  if (!isHeaderLogo && !isProduct) {
    sessionStorage.setItem('rakiNoIntroNextPage', 'true');
  }
}, true);

/* =========================================================
   RAKI STUDIOS — NEVER REPLAY INTRO AFTER INTERNAL LINK
   ========================================================= */

document.addEventListener(
  'click',
  (event) => {
    const link = event.target.closest('a[href]');

    if (!link) return;

    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.target === '_blank' ||
      link.hasAttribute('download')
    ) {
      return;
    }

    let destination;

    try {
      destination = new URL(
        link.href,
        window.location.href
      );
    } catch {
      return;
    }

    /* Alleen links binnen eigen webshop */
    if (
      destination.origin !== window.location.origin
    ) {
      return;
    }

    /* Alleen als er daadwerkelijk naar een andere URL wordt gegaan */
    if (
      destination.pathname === window.location.pathname &&
      destination.search === window.location.search &&
      destination.hash
    ) {
      return;
    }

    /*
     * De volgende pagina mag de lange opening-intro
     * NIET opnieuw afspelen.
     */
    sessionStorage.setItem(
      'rakiSkipIntroOnce',
      '1'
    );
  },
  true
);

/* RAKI — first-visit state and staggered New Arrivals reveal */
(() => {
  const root = document.documentElement;
  const isOpeningVisit = root.classList.contains('raki-intro-first');

  try {
    sessionStorage.setItem('raki-intro-seen', 'true');
  } catch (error) {
    // Keep the theme's existing fallback when browser storage is unavailable.
  }

  if (!isOpeningVisit || window.location.pathname !== '/') return;

  try {
    if (sessionStorage.getItem('raki-arrivals-revealed') === 'true') return;
    sessionStorage.setItem('raki-arrivals-revealed', 'true');
  } catch (error) {
    // The reveal can still run once for this page load without storage.
  }

  const mountReveal = () => {
    const heading = Array.from(document.querySelectorAll('h1, h2, h3')).find(
      (element) =>
        element.textContent.replace(/\s+/g, ' ').trim().toLowerCase() ===
        'new arrivals'
    );
    const section = heading?.closest('.shopify-section, section');
    const cards = Array.from(
      section?.querySelectorAll('li.grid__item') || []
    ).slice(0, 8);

    if (!section || cards.length === 0) return;

    cards.forEach((card, index) => {
      card.classList.add('raki-arrival-entry');
      card.style.setProperty('--raki-arrival-order', index);
    });

    const reveal = () => {
      cards.forEach((card) => card.classList.add('is-revealed'));

      window.setTimeout(() => {
        cards.forEach((card) => {
          card.classList.remove('raki-arrival-entry', 'is-revealed');
          card.style.removeProperty('--raki-arrival-order');
        });
      }, 2200);
    };

    if (!('IntersectionObserver' in window)) {
      reveal();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        reveal();
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    observer.observe(section);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountReveal, { once: true });
  } else {
    mountReveal();
  }
})();

/* RAKI audit fixes — 2026-09-05 */
(() => {
  const mountRakiAuditFixes = () => {
    document.querySelectorAll('.raki-collections-details > summary').forEach((summary) => {
      if (summary.dataset.rakiClickReady === 'true') return;
      summary.dataset.rakiClickReady = 'true';

      summary.addEventListener('click', (event) => {
        const details = summary.parentElement;
        if (!(details instanceof HTMLDetailsElement)) return;

        const shouldOpen = !details.open;
        event.preventDefault();
        event.stopPropagation();

        document.querySelectorAll('.raki-collections-details').forEach((item) => {
          const isOpen = item === details && shouldOpen;
          item.toggleAttribute('open', isOpen);
          item.querySelector('summary')?.setAttribute('aria-expanded', String(isOpen));
        });

        document.body.classList.toggle('raki-mega-open', shouldOpen);
      });
    });

    document.querySelectorAll('footer a').forEach((link) => {
      link.querySelectorAll('span').forEach((span) => {
        if (/^\d{2}$/.test(span.textContent.trim())) {
          span.setAttribute('aria-hidden', 'true');
        }
      });
    });

    const cookieLink = Array.from(document.querySelectorAll('footer a')).find((link) =>
      link.href.includes('shopifyReshowConsentBanner')
    );
    if (cookieLink) {
      cookieLink.setAttribute('href', '#shopifyReshowConsentBanner');
    }

    if (window.location.pathname === '/pages/contact') {
      document.querySelector('.raki-contact__headline h1')?.setAttribute('aria-label', "Let's talk.");
      document.querySelector('.raki-contact__aside h2')?.setAttribute('aria-label', 'How can we help?');
    }

    document.querySelectorAll('predictive-search').forEach((search) => {
      const cleanSearchCopy = () => {
        const walker = document.createTreeWalker(search, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          node.nodeValue = node.nodeValue
            .replace(/\u00e2\u20ac\u0153/g, '“')
            .replace(/\u00e2\u20ac\u009d/g, '”')
            .replace(/\u00e2\u20ac/g, '”');
        }
      };

      cleanSearchCopy();
      new MutationObserver(cleanSearchCopy).observe(search, {
        childList: true,
        subtree: true,
      });
    });
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll('.raki-collections-details[open]').forEach((details) => {
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
    document.body.classList.remove('raki-mega-open');
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountRakiAuditFixes, { once: true });
  } else {
    mountRakiAuditFixes();
  }
})();

/* Keep desktop mega menus operable by click, Enter and Space. */
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const summary = event.target instanceof Element
      ? event.target.closest('.raki-collections-details > summary')
      : null;
    if (!summary) return;

    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const shouldOpen = !details.open;
    document.querySelectorAll('.raki-collections-details').forEach((item) => {
      const isOpen = item === details && shouldOpen;
      item.toggleAttribute('open', isOpen);
      item.querySelector('summary')?.setAttribute('aria-expanded', String(isOpen));
    });
    document.body.classList.toggle('raki-mega-open', shouldOpen);
  },
  true
);

document.addEventListener(
  'keyup',
  (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.raki-collections-details > summary')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const summary = event.target instanceof Element
      ? event.target.closest('.raki-collections-details > summary')
      : null;
    if (!summary) return;

    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const shouldOpen = !details.open;
    document.querySelectorAll('.raki-collections-details').forEach((item) => {
      const isOpen = item === details && shouldOpen;
      item.toggleAttribute('open', isOpen);
      item.querySelector('summary')?.setAttribute('aria-expanded', String(isOpen));
    });
    document.body.classList.toggle('raki-mega-open', shouldOpen);
  },
  true
);


/* =========================================================
   RAKI — FINAL MEGA MENU OVERRIDE
   Products / Collections / About independently controlled
   ========================================================= */

(() => {
  const menus = [
    {
      trigger: '.raki-products-details',
      panel: '#RakiProductsMenu'
    },
    {
      trigger: '.raki-collections-details',
      panel: '#RakiCollectionsMenu'
    },
    {
      trigger: '.raki-about-details',
      panel: '#RakiAboutMenu'
    }
  ];

  const isDesktop = () => window.innerWidth >= 990;

  function hidePanel(panel) {
    if (!panel) return;

    panel.style.setProperty('opacity', '0', 'important');
    panel.style.setProperty('visibility', 'hidden', 'important');
    panel.style.setProperty('pointer-events', 'none', 'important');
  }

  function showPanel(panel) {
    if (!panel) return;

    panel.style.setProperty('opacity', '1', 'important');
    panel.style.setProperty('visibility', 'visible', 'important');
    panel.style.setProperty('pointer-events', 'auto', 'important');
  }

  function closeTrigger(trigger) {
    if (!trigger) return;

    trigger.removeAttribute('open');

    const summary = trigger.querySelector('summary');

    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
    }
  }

  function openTrigger(trigger) {
    if (!trigger) return;

    trigger.setAttribute('open', '');

    const summary = trigger.querySelector('summary');

    if (summary) {
      summary.setAttribute('aria-expanded', 'true');
    }
  }

  function closeAll(except = null) {
    menus.forEach(item => {
      const trigger = document.querySelector(item.trigger);
      const panel = document.querySelector(item.panel);

      if (except && item.trigger === except.trigger) {
        return;
      }

      closeTrigger(trigger);
      hidePanel(panel);
    });
  }

  function openMenu(item) {
    if (!isDesktop()) return;

    closeAll(item);

    const trigger = document.querySelector(item.trigger);
    const panel = document.querySelector(item.panel);

    openTrigger(trigger);
    showPanel(panel);
  }

  function bindMenus() {
    menus.forEach(item => {
      const trigger = document.querySelector(item.trigger);
      const panel = document.querySelector(item.panel);

      if (!trigger || !panel) return;

      if (trigger.dataset.rakiFinalBound === 'true') return;

      trigger.dataset.rakiFinalBound = 'true';

      trigger.addEventListener('mouseenter', () => {
        openMenu(item);
      });

      panel.addEventListener('mouseenter', () => {
        openMenu(item);
      });
    });
  }

  document.addEventListener('mousemove', event => {
    if (!isDesktop()) return;

    let activePanel = null;

    menus.forEach(item => {
      const panel = document.querySelector(item.panel);

      if (!panel) return;

      const styles = window.getComputedStyle(panel);

      if (
        styles.visibility !== 'hidden' &&
        parseFloat(styles.opacity || '0') > 0
      ) {
        activePanel = panel;
      }
    });

    if (!activePanel) return;

    const rect = activePanel.getBoundingClientRect();

    /*
     * Zodra cursor ONDER de zichtbare dropdown komt:
     * alles sluiten.
     */
    if (event.clientY > rect.bottom + 2) {
      closeAll();
    }
  });

  document.addEventListener('click', event => {
    if (!isDesktop()) return;

    const clickedInsideMenu = menus.some(item => {
      const trigger = document.querySelector(item.trigger);
      const panel = document.querySelector(item.panel);

      return (
        trigger?.contains(event.target) ||
        panel?.contains(event.target)
      );
    });

    if (!clickedInsideMenu) {
      closeAll();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindMenus);
  } else {
    bindMenus();
  }

  document.addEventListener('shopify:section:load', bindMenus);
})();